"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"

import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

// Radix Select items can't carry "", hence the sentinels.
const NONE = "__none__"
const OTHER = "__other__"

export const VISIT_PURPOSE_MAX_LENGTH = 100

interface VisitPurposeSelectProps {
  /** "" = no purpose; otherwise a config/visit_purposes.php key or free text. */
  value: string
  onChange: (value: string) => void
  suggested: string[]
  others: string[]
  /** Headings for the two groups; omitted → one flat list (suggested first). */
  groupLabels?: { suggested: string; other: string }
  disabled?: boolean
  triggerClassName?: string
}

/**
 * Purpose dropdown for trips and clinical visits (Visit Categorization).
 *
 * The config keys are the usual list; "Other…" at the bottom opens a text box
 * so the desk can write a purpose the list doesn't have (Dentour 260923). A
 * stored value that isn't one of the keys IS such free text, so editing it
 * reopens in "Other" mode with the text filled in.
 */
export function VisitPurposeSelect({
  value,
  onChange,
  suggested,
  others,
  groupLabels,
  disabled,
  triggerClassName,
}: VisitPurposeSelectProps) {
  const t = useTranslations("visitCategorization")
  const [otherChosen, setOtherChosen] = useState(false)

  const known = [...suggested, ...others]
  // While the key list is still loading (empty), don't mistake a saved key for
  // free text — it would flash the text box with "kirurgji" in it.
  const isOther = otherChosen || (value !== "" && known.length > 0 && !known.includes(value))

  const label = (key: string) => (t.has(`purposes.${key}`) ? t(`purposes.${key}`) : key)

  const handleSelect = (next: string) => {
    if (next === OTHER) {
      setOtherChosen(true)
      // Keep text already typed; a key picked earlier is not free text.
      if (known.includes(value)) onChange("")
      return
    }
    setOtherChosen(false)
    onChange(next === NONE ? "" : next)
  }

  return (
    <div className="space-y-1.5">
      <Select
        value={isOther ? OTHER : value || NONE}
        onValueChange={handleSelect}
        disabled={disabled}
      >
        <SelectTrigger className={cn("w-full", triggerClassName)}>
          <SelectValue placeholder={t("tab.purposePlaceholder")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>
            <span className="text-muted-foreground">{t("tab.noPurpose")}</span>
          </SelectItem>
          {groupLabels ? (
            <>
              {suggested.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{groupLabels.suggested}</SelectLabel>
                  {suggested.map((key) => (
                    <SelectItem key={key} value={key}>
                      {label(key)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {others.length > 0 && (
                <SelectGroup>
                  <SelectLabel>{groupLabels.other}</SelectLabel>
                  {others.map((key) => (
                    <SelectItem key={key} value={key}>
                      {label(key)}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </>
          ) : (
            known.map((key) => (
              <SelectItem key={key} value={key}>
                {label(key)}
              </SelectItem>
            ))
          )}
          <SelectSeparator />
          <SelectItem value={OTHER}>{t("tab.otherPurpose")}</SelectItem>
        </SelectContent>
      </Select>
      {isOther && (
        <Input
          value={value}
          onChange={(event) => {
            // Lock "Other" mode: clearing the box must not hide it mid-edit.
            setOtherChosen(true)
            onChange(event.target.value)
          }}
          maxLength={VISIT_PURPOSE_MAX_LENGTH}
          placeholder={t("tab.otherPurposePlaceholder")}
          aria-label={t("tab.otherPurposePlaceholder")}
          disabled={disabled}
          autoFocus={otherChosen}
          className={triggerClassName}
        />
      )}
    </div>
  )
}
